import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {Hub, TrelloAttachmentsResp} from 'yougile-model'
import {Backend} from '@common/backend'
import {MessageBox} from '@components/message-box'
import {BoardOption} from '../import-from-trello-store'
import {TrelloBoardData} from './create-trello-project'
import {
  Attachments,
  TrelloAction,
  TrelloBoard,
  TrelloError,
  TrelloMe
} from './trello-board'

const fetchLimit = 1000
export const trelloReAuthRequired = 'Trello re-authorization required'
const ATTACHMENTS_CHUNK_SIZE = 3_000

const fetchData = async <T>(url: string, filter?: string): Promise<T> => {
  const cred = `key=${window.Trello.key()}&token=${window.Trello.token()}`
  const data = `${url}?${cred}${filter ?? ''}`
  const fetchResult = await fetch(data)
  if (fetchResult.status === 401) {
    const error = new Error()
    error.name = trelloReAuthRequired
    error.message = trelloReAuthRequired
    throw error
  }
  return fetchResult.json()
}

export const fetchBoardFullName = async (): Promise<string> => {
  const result = await fetchData<TrelloMe>(
    'https://api.trello.com/1/members/me/fullName'
  )
  return result._value
}

const fetchBoardActions = async (
  boardId: string,
  filter: string[],
  boardActions: TrelloAction[] = [],
  actionId?: TrelloAction['id'],
  setLoadingProgress?: (perc: number) => void,
  currentProgress = 0,
  fetchProgressSize = 0,
  iteration = 0
): Promise<TrelloAction[]> => {
  try {
    const filters = `&limit=${fetchLimit}&filter=${filter.join(',')}${
      actionId ? `&before=${actionId}` : ''
    }`
    const data = `https://trello.com/1/boards/${boardId}/actions`
    const boardActionsData = await fetchData<TrelloAction[]>(data, filters)
    const board = [...boardActions, ...boardActionsData]

    if (boardActionsData.length !== fetchLimit) {
      return board
    } else {
      iteration++
      currentProgress += fetchProgressSize / 2 ** iteration
      setLoadingProgress?.(currentProgress)
      const lastId = boardActionsData[boardActionsData.length - 1].id
      return fetchBoardActions(
        boardId,
        filter,
        board,
        lastId,
        setLoadingProgress,
        currentProgress,
        fetchProgressSize,
        iteration
      )
    }
  } catch (e) {
    logger.error('Failed to load boards actions', e)
    MessageBox.show(
      Lang.msg.importFromTrello.failedToLoadBoardsActions,
      'danger',
      {timer: -1}
    )
    return []
  }
}

export const fetchBoardOptions = async (): Promise<BoardOption[]> => {
  const boards = await fetchData<TrelloBoard[]>(
    'https://api.trello.com/1/members/me/boards'
  )

  return boards
    .map(
      (board): BoardOption => ({
        label: board.name,
        value: board.shortUrl,
        backgroundImage:
          board.prefs.backgroundImageScaled?.[
            Math.round(board.prefs.backgroundImageScaled?.length / 2)
          ]?.url,
        backgroundColor: board.prefs.backgroundColor
      })
    )
    .filter(Boolean)
}

const getTrelloAttachmentUrls = (
  attachments: TrelloAction[]
): Attachments[] => {
  return attachments
    .map(attachment => ({
      url: attachment?.data?.attachment?.url,
      previewUrl:
        attachment?.data?.attachment?.previewUrl2x ||
        attachment?.data?.attachment?.previewUrl ||
        'none',
      cardId: attachment?.data?.card?.id,
      attachmentId: attachment.data.attachment?.id
    }))
    .filter(
      att => att?.url && att?.previewUrl && att?.cardId && att?.attachmentId
    ) as Attachments[]
}

const getTrelloCoverUrls = (boardData: TrelloBoard): Attachments[] => {
  return boardData.cards
    .map(card => {
      const unsplash = !!card.cover.sharedSourceUrl?.startsWith(
        'https://images.unsplash.com'
      )
      const attachment =
        !unsplash && card.attachments.find(a => a.id === card.idAttachmentCover)
      const trelloUrl = !!(attachment && checkOnTrelloUrl(attachment.url))
      const url = trelloUrl ? attachment.url : card.cover.sharedSourceUrl ?? ''
      return (
        (trelloUrl || unsplash) && {
          url,
          previewUrl: 'none',
          cardId: card?.id,
          attachmentId: trelloUrl ? attachment.id : 'unsplash'
        }
      )
    })
    .filter(url => !!url) as Attachments[]
}

const checkOnTrelloUrl = (url: string): boolean =>
  url.startsWith('https://trello.com')

const isValidHttpUrl = (string: string): boolean => {
  let url
  try {
    url = new URL(string)
  } catch (_) {
    return false
  }
  return url.protocol === 'http:' || url.protocol === 'https:'
}

const saveTrelloAttachments = async (
  attachments: TrelloAction[]
): Promise<Attachments[]> => {
  const urls = getTrelloAttachmentUrls(attachments)
  const trelloUrls = []
  const otherUrls = []
  for (const url of urls) {
    if (!url.url) {
      continue
    }
    if (checkOnTrelloUrl(url.url)) {
      trelloUrls.push(url)
    } else if (isValidHttpUrl(url.url)) {
      otherUrls.push(url)
    }
  }
  if (trelloUrls.length) {
    let data: Attachments[] = []
    for (let i = 0; i < trelloUrls.length; i += ATTACHMENTS_CHUNK_SIZE) {
      const resp = await Backend.postJSON<TrelloAttachmentsResp>(
        '/data/trello-file-downloader',
        {
          token: window.Trello.token(),
          key: window.Trello.key(),
          urls: trelloUrls.slice(i, i + ATTACHMENTS_CHUNK_SIZE),
          userId: Backend.loadId(),
          userKey: Backend.loadKey()
        },
        {useCurrentHost: true}
      ).catch(e => ({error: e}))
      if (!('error' in resp)) {
        data = data.concat(resp?.data)
      } else {
        logger.error(resp.error)
        return [] as Attachments[]
      }
    }

    return data.concat(otherUrls as Attachments[])
  } else {
    return otherUrls as Attachments[]
  }
}

const saveTrelloCovers = async (
  boardData: TrelloBoard
): Promise<Attachments[]> => {
  const urls = getTrelloCoverUrls(boardData)
  if (!urls.length) {
    return []
  }

  let data: Attachments[] = []
  for (let i = 0; i < urls.length; i += ATTACHMENTS_CHUNK_SIZE) {
    const urlsPart = urls.slice(i, i + ATTACHMENTS_CHUNK_SIZE)
    const resp = await Backend.postJSON<TrelloAttachmentsResp>(
      '/data/trello-file-downloader',
      {
        token: window.Trello.token(),
        key: window.Trello.key(),
        urls: urlsPart,
        userId: Backend.loadId(),
        userKey: Backend.loadKey()
      },
      {useCurrentHost: true}
    ).catch(e => ({error: e}))
    if (!('error' in resp)) {
      data = data.concat(resp?.data)
    } else {
      logger.error(resp.error)
      return [] as Attachments[]
    }
  }

  return data
}

const isTrelloError = (
  data: TrelloBoard | TrelloError
): data is TrelloError => {
  return !!(data as TrelloError).originalMessage
}

export const fetchBoard = async (
  shortUrl: string,
  board: Hub,
  setLoadingProgress: (perc: number) => void,
  currentProgress: number,
  fetchProgressSize: number
): Promise<TrelloBoardData | undefined> => {
  const filters = '&lists=all&cards=all&card_attachments=cover&checklists=all'
  const data = `${shortUrl}.json`
  const boardData = await fetchData<TrelloBoard | TrelloError>(data, filters)
  if (boardData instanceof Error || isTrelloError(boardData)) {
    logger.error(`Failed to load board ${shortUrl}`, boardData)
    MessageBox.show(
      `${Lang.msg.importFromTrello.failedToLoadBoards}. ${
        Lang.msg.importFromTrello.errorFromTrello
      }: ${(boardData as TrelloError).originalMessage}. ${
        Lang.msg.importFromTrello.fixThisAndTryAgain
      }`,
      'danger',
      {timer: -1}
    )
    return undefined
  }

  currentProgress += 0.1 * fetchProgressSize
  setLoadingProgress(currentProgress)
  const fetchActionsProgressSize = 0.4 * fetchProgressSize
  const boardActions = await fetchBoardActions(
    boardData.id,
    ['commentCard', 'createCard'],
    undefined,
    undefined,
    setLoadingProgress,
    currentProgress,
    fetchActionsProgressSize
  )

  currentProgress += fetchActionsProgressSize
  setLoadingProgress(currentProgress)
  const boardAttachmentsData = await fetchBoardActions(
    boardData.id,
    ['addAttachmentToCard'],
    undefined,
    undefined,
    setLoadingProgress,
    currentProgress,
    fetchActionsProgressSize
  )
  currentProgress += fetchActionsProgressSize
  setLoadingProgress(currentProgress)
  const boardAttachments = await saveTrelloAttachments(boardAttachmentsData)
  const covers = await saveTrelloCovers(boardData)

  return {
    boardData,
    board,
    boardActions,
    attachments: boardAttachments,
    covers
  }
}
